import { Link20Regular } from "@fluentui/react-icons";
import { useRichTextEditorContext } from "mui-tiptap";
import { useState, useEffect, useCallback } from "react";
import { Button, Tooltip } from "@mui/material";
import LinkDialog from "./LinkDialog";
import { Tool } from "../../models/Tool";

type MenuButtonEditLinkCustomProps = {
  tooltipLabel: string;
  ariaLabel: string;
  onLinkInsert?: (tool: Tool) => void;
  onLinkRemove?: (toolId: string) => void;
  selectedTools?: Tool[];
};

export default function MenuButtonEditLinkCustom({
  tooltipLabel,
  ariaLabel,
  onLinkInsert,
  onLinkRemove,
  selectedTools,
}: MenuButtonEditLinkCustomProps) {
  const editor = useRichTextEditorContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentLink, setCurrentLink] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState<string>("");

  useEffect(() => {
    if (!editor) return;

    let previousToolLinkIds: Set<string> = new Set();

    const updateLinkState = () => {
      // Get current link if in a link
      if (editor.isActive("link")) {
        const attrs = editor.getAttributes("link");
        setCurrentLink(attrs.href || null);
      } else {
        setCurrentLink(null);
      }

      // Track tool link removals
      if (onLinkRemove) {
        const currentToolLinkIds = new Set<string>();
        const { state } = editor;

        state.doc.descendants((node) => {
          if (node.marks) {
            node.marks.forEach((mark) => {
              if (
                mark.type.name === "link" &&
                mark.attrs["data-tool-link-id"]
              ) {
                currentToolLinkIds.add(mark.attrs["data-tool-link-id"]);
              }
            });
          }
        });

        // Find removed tool links
        previousToolLinkIds.forEach((toolId) => {
          if (!currentToolLinkIds.has(toolId)) {
            onLinkRemove(toolId);
          }
        });

        previousToolLinkIds = currentToolLinkIds;
      }
    };

    editor.on("selectionUpdate", updateLinkState);
    editor.on("transaction", updateLinkState);
    updateLinkState();

    return () => {
      editor.off("selectionUpdate", updateLinkState);
      editor.off("transaction", updateLinkState);
    };
  }, [editor, onLinkRemove]);

  const handleClick = useCallback(() => {
    // Capture selected text when opening dialog
    if (editor) {
      const { state } = editor;
      const { selection } = state;
      const text = state.doc.textBetween(selection.from, selection.to, "");
      setSelectedText(text);
    }
    setDialogOpen(true);
  }, [editor]);

  const handleApply = useCallback(
    (url: string, fullTool?: Tool) => {
      if (!editor) return;

      const { state } = editor;
      const { selection } = state;
      const hasSelection = !selection.empty;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const linkAttrs: Record<string, any> = {
        href: url,
        target: "_blank",
        rel: "noopener noreferrer",
      };

      if (fullTool?.toolId) {
        linkAttrs["data-tool-link-id"] = fullTool.toolId;
      }

      if (currentLink) {
        // Update existing link
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .updateAttributes("link", linkAttrs)
          .run();
      } else if (hasSelection) {
        // Text is selected - apply link to selection
        editor
          .chain()
          .focus()
          .setLink({
            href: url,
            target: "_blank",
            rel: "noopener noreferrer",
          })
          .run();

        // If we have a toolId, update the attributes to include it
        if (fullTool?.toolId) {
          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .updateAttributes("link", { "data-tool-link-id": fullTool.toolId })
            .run();
        }
      } else {
        // No selection - insert link with text
        const linkText = fullTool?.title || url;
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            marks: [
              {
                type: "link",
                attrs: linkAttrs,
              },
            ],
            text: linkText,
          })
          .run();
      }

      // Notify parent if it's a tool link
      if (fullTool && onLinkInsert) {
        onLinkInsert(fullTool);
      }
    },
    [editor, currentLink, onLinkInsert]
  );

  return (
    <>
      <Tooltip title={tooltipLabel}>
        <Button
          aria-label={ariaLabel}
          disabled={!editor?.isEditable}
          onClick={handleClick}
          sx={{
            minWidth: "auto",
            padding: "4px 8px",
            color: currentLink ? "primary.main" : "text.primary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Link20Regular />
        </Button>
      </Tooltip>
      <LinkDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onApply={handleApply}
        currentLink={currentLink}
        selectedText={selectedText}
        selectedTools={selectedTools}
      />
    </>
  );
}
